<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 16/12/2021
  -->

<template>
	<div class="card p-2 " :style="{'max-height': isLegendShown ? 'calc(80vh - 200px)' : 'initial', 'min-height': isLegendShown ? '14rem' : '2.8rem'}"
	 v-if="hasLegendItemsToShow">
		<div class="legend-header" :class="{'legend-header--collapsed': !isLegendShown}">
			<div class="qw-row legend-header__top">
				<div class="legend-header-copy">
					<div class="legend-header-copy__title">Interactive legend</div>
				</div>
				<div class="layout-spacer"></div>
				<div class="legend-toggle-action">
					<qw-link-button @click="changeLegendVisibility">{{ hideLegendButtonTitle }}</qw-link-button>
				</div>
			</div>
			<div class="legend-header__bottom">
				<div v-if="hasLockedLegendItemsComputed">
					<qw-link-button @click="clearLockedLegendItem">Clear selection</qw-link-button>
				</div>
				<div v-else class="legend-header-copy__helper">{{ legendHelperTextComputed }}</div>
			</div>
		</div>

		<div class="overflow-y"

			 :style="{'width': legendWidth}"
			 v-if="isLegendShown">

			<div id="legendContent" class="p-2">

				<div class="mb-4" v-if="familyConnectionsArray.length">
					<div class="legend-section-title mb-2">Family Relationships</div>
					<div class="qw-row">
						<div
							v-for="(lineTypeWrapper, index) of familyConnectionsArray"
							:key="'line_type_' + index"
							class="col-6 font-size-2 px-1 legend-row-item"
							:class="getLegendRowClass(lineTypeWrapper.hoverItem)"
							@mouseenter="onLegendHoverStart(lineTypeWrapper.hoverItem)"
							@mouseleave="onLegendHoverEnd"
							@click="onLegendLockToggle(lineTypeWrapper.hoverItem)"
						>
							<LineTypeItemView
								:isViewOnly="true"
								:line-type-item="lineTypeWrapper.lineTypeItem"
								:title-prefix="showFamilyConnectionsCount ? lineTypeWrapper.count + 'x': ''"/>
						</div>
					</div>
				</div>
				<div class="mb-4" v-if="emotionalConnectionsArray.length">
					<div class="legend-section-title mb-2">Emotional Relationships</div>
					<div class="qw-row">
						<div
							v-for="(lineTypeWrapper, index) of emotionalConnectionsArray"
							:key="'line_type_' + index"
							class="col-6 font-size-2 px-1 legend-row-item"
							:class="getLegendRowClass(lineTypeWrapper.hoverItem)"
							@mouseenter="onLegendHoverStart(lineTypeWrapper.hoverItem)"
							@mouseleave="onLegendHoverEnd"
							@click="onLegendLockToggle(lineTypeWrapper.hoverItem)"
						>
							<LineTypeItemView
								:isViewOnly="true"
								:line-type-item="lineTypeWrapper.lineTypeItem"
								:title-prefix="showEmotionalConnectionsCount ? lineTypeWrapper.count + 'x' : ''"/>
						</div>
					</div>
				</div>
				<div v-if="healthConditionItems.length">
					<div class="legend-section-title mb-2">Health Conditions</div>
					<div class="qw-row">
						<div v-for="(healthCondition, index) of healthConditionItems" :key="'health_' + index"
							 class="col-12 legend-row-item"
							 :class="getLegendRowClass(healthCondition.hoverItem)"
							 style="padding-left: 10px"
							 @mouseenter="onLegendHoverStart(healthCondition.hoverItem)"
							 @mouseleave="onLegendHoverEnd"
							 @click="onLegendLockToggle(healthCondition.hoverItem)">
							<div class="health-condition-row">
								<div class="health-condition-dot" :style="{backgroundColor: healthCondition.color}"></div>
								<div class="health-condition-text"><span v-if="showHealthConditionsCount" class="">{{ healthCondition.count }}x</span>
									{{ healthCondition.title }}
								</div>
							</div>
						</div>
					</div>

				</div>
				<div v-if="groupItems.length" class="mt-4">
					<div class="legend-section-title mb-2">Groups</div>
					<div class="qw-row">
						<div v-for="(group, index) of groupItems" :key="'group_' + index"
							 class="col-12 legend-row-item"
							 :class="getLegendRowClass(group.hoverItem)"
							 style="padding-left: 10px"
							 @mouseenter="onLegendHoverStart(group.hoverItem)"
							 @mouseleave="onLegendHoverEnd"
							 @click="onLegendLockToggle(group.hoverItem)">
							<div class="health-condition-row">
								<div class="health-condition-dot" :style="{backgroundColor: group.color}"></div>
								<div class="health-condition-text">{{ group.title }}</div>
							</div>
						</div>
					</div>

				</div>
				<div v-if="symbolItems.length" class="mt-4">
					<div class="legend-section-title mb-2">Symbols</div>
					<div class="qw-row">
						<div v-for="(symbol, index) of symbolItems" :key="'symbol_' + index"
							 class="col-12 symbol-item legend-row-item"
							 :class="getLegendRowClass(symbol.hoverItem)"
							 style="padding-left: 10px"
							 @mouseenter="onLegendHoverStart(symbol.hoverItem)"
							 @mouseleave="onLegendHoverEnd"
							 @click="onLegendLockToggle(symbol.hoverItem)">
							<div class="symbol-row">
								<GenogramLegendSymbolIcon
									:src="symbol.src"
									:alt="symbol.title"
									:type="symbol.type"
									:size="28"
									:is-primary-person="symbol.isPrimaryPerson"
									:is-passed-away="symbol.isPassedAway"
									:sexual-communication="symbol.sexualCommunication"
								/>
								<div class="symbol-item-text">
									<span v-if="shouldShowSymbolCount(symbol)">{{ symbol.count }}x</span>
									{{ symbol.title }}
								</div>
							</div>
						</div>
					</div>

				</div>
			</div>

		</div>

	</div>
</template>

<script>


import CyPropWatchingView
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/CyPropWatchingView.vue";
import LineTypeItemView
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/LineTypeItemView.vue";
import {
	findEmotionalLineTypeItemByLineType,
	findFamilyLineTypeItemByLineType,
	resolveEmotionalLineTypeItemByLineType,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/LineTypeItem";
import {
	nodeTypeToImage,
	nodeTypesSymbols,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/defaults/node-types";
import GenogramItem from "@shared/Genogram/GenogramItem";
import GenogramLegendSymbolIcon from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/GenogramLegendSymbolIcon.vue";
import {
	isPrimaryPersonNodeData,
	PRIMARY_PERSON_LABEL,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramPrimaryPersonHelper";
import {buildPassedAwayLegendItems} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendSpecialSymbolHelper";
import {
	applyLegendInteractionState,
	clearLegendHover,
	getLegendHoverItemKey,
	LEGEND_HOVER_KIND,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendHoverHelper";
import GenogramCustomConnectionsSyncManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramCustomConnectionsSyncManager";
import {
	buildHealthConditionLegendItems,
	shouldShowLegendItemCount,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendCountHelper";
import {
	getSexualCommunicationLegendLabel,
	hasSexualCommunicationValue,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramSexualCommunicationHelper";
let html2canvasImport = () => import("html2canvas");

export default {
	name: "GenogramLegendView",
	components: {GenogramLegendSymbolIcon, LineTypeItemView},
	extends: CyPropWatchingView,

	props: {
		genogramItem: {GenogramItem, required: true},
		legendWidth: String,
		legendInitialVisibility: {Boolean, default: true},
		customConnectionsSyncManager: {type: GenogramCustomConnectionsSyncManager, default: null},
	},

	data() {
		return {
			isLegendShown: true,
			showEmotionalConnectionsCount: false,
			showFamilyConnectionsCount: false,
			showHealthConditionsCount: false,
			showSymbolsCount: false,
			emotionalConnectionsArray: [],
			familyConnectionsArray: [],
			lineTypeItems: [],
			healthConditionItems: [],
			groupItems: [],
			symbolItems: [],
			hoveredLegendItemKey: "",
			lockedLegendItems: [],
		}
	},

	mounted() {
		this.isLegendShown = this.legendInitialVisibility
	},

	beforeUnmount() {
		this.clearLockedLegendItem()
	},

	methods: {

		onCyReady() {
			this.cy.on("cyeditor.added cyeditor.remove cyeditor.data cyeditor.linetype", e => {
				this.recalculateLegend()
			})
			this.recalculateLegend()
		},

		recalculateLegend() {
			this.recalculateConnectionTypesLegend()
			this.recalculateHealthConditionsLegend()
			this.recalculateGroupsLegend()
			this.recalculateSymbolsLegend()
		},

		recalculateConnectionTypesLegend() {
			let edges = this.cy.edges()
			// console.log("recalculateConnectionTypesLegend: ", edges)
			let familyConnectionsArray = []
			let emotionalConnectionsArray = []
			for (let edge of edges) {
				let {lineSubType} = edge.data()
				this.setLineTypeItem(lineSubType, familyConnectionsArray, findFamilyLineTypeItemByLineType)
			this.setLineTypeItem(lineSubType, emotionalConnectionsArray, (value) => resolveEmotionalLineTypeItemByLineType(value, this.customConnectionsSyncManager))
			}
			familyConnectionsArray.forEach(item => {
				if (item.count > 1) {
					this.showFamilyConnectionsCount = true
				}
			})
			emotionalConnectionsArray.forEach(item => {
				if (item.count > 1) {
					this.showEmotionalConnectionsCount = true
				}
			})
			familyConnectionsArray.sort((l, r) => r.count - l.count)
			emotionalConnectionsArray.sort((l, r) => r.count - l.count)
			this.familyConnectionsArray = familyConnectionsArray
			this.emotionalConnectionsArray = emotionalConnectionsArray
		},

		setLineTypeItem(lineSubType, destArray, searchFunction) {
			let lineTypeItem = searchFunction(lineSubType)
			if (lineTypeItem){// && lineTypeItem !== straightLine) {
				let index = destArray.findIndex(item => item.lineTypeItem.lineType === lineTypeItem.lineType)
				if (index < 0) {
					let lineTypeItemWrapper = {
						count: 1,
						lineTypeItem,
						hoverItem: {
							kind: LEGEND_HOVER_KIND.LINE_SUB_TYPE,
							lineSubType: lineTypeItem.lineType,
						},
					}
					destArray.push(lineTypeItemWrapper)
				} else {
					destArray[index].count++
				}
			}

		},

		recalculateHealthConditionsLegend() {
			let nodes = this.cy.nodes()
			let healthConditionItems = buildHealthConditionLegendItems(nodes, (node) => node.data()).map((item) => ({
				...item,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.HEALTH_CONDITION,
					title: item.title,
					color: item.color,
				},
			}))
			this.showHealthConditionsCount = shouldShowLegendItemCount(healthConditionItems)
			this.healthConditionItems = healthConditionItems

		},

		recalculateSymbolsLegend() {
			let symbolItems = []
			this.showSymbolsCount = false
			let nodes = this.cy.nodes()
			for (let node of nodes) {
				if(node.data("isAuxNode") || node.data("excludeFromLegend")){
					continue
				}
				if(node.data("type") === "triangle" && node.data("isPassedAway")){
					continue
				}
				this.setSymbolItem(node.data("type"), symbolItems)
				this.setSexualCommunicationItem(node.data("sexualCommunication"), symbolItems)
			}
			symbolItems.sort((left, right) => left.title.localeCompare(right.title))
			let primaryPersonItem = this.buildPrimaryPersonSymbolItem(nodes)
			if(primaryPersonItem){
				symbolItems.unshift(primaryPersonItem)
			}
			symbolItems.push(...this.buildPassedAwaySymbolItems(nodes))
			this.showSymbolsCount = shouldShowLegendItemCount(symbolItems)
			this.symbolItems = symbolItems
		},

		buildPassedAwaySymbolItems(nodes = []) {
			return buildPassedAwayLegendItems(nodes, (node) => node.data()).map((item) => ({
				...item,
				hoverItem: {
					kind: item.type === "triangle" ? LEGEND_HOVER_KIND.PASSED_AWAY_PREGNANCY : LEGEND_HOVER_KIND.PASSED_AWAY,
				},
			}))
		},

		shouldShowSymbolCount(symbol) {
			return this.showSymbolsCount && Number(symbol?.count) > 0
		},

		setSymbolItem(type, destArray, options = {}) {
			let symbolDefinition = nodeTypesSymbols.find(nodeType => nodeType.type === type)
			let src = nodeTypeToImage[type]
			if(!symbolDefinition || !src){
				return
			}
			let index = destArray.findIndex(item => item.type === type && item.isPrimaryPerson === !!options.isPrimaryPerson)
			if(index >= 0){
				destArray[index].count++
				return
			}
			destArray.push({
				type,
				src,
				title: options.title || symbolDefinition.label.replaceAll("<br/>", " "),
				count: 1,
				isPrimaryPerson: !!options.isPrimaryPerson,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.SYMBOL_TYPE,
					symbolType: type,
				},
			})
		},

		setSexualCommunicationItem(sexualCommunication, destArray) {
			if(!hasSexualCommunicationValue(sexualCommunication)){
				return
			}
			let index = destArray.findIndex(item => item.sexualCommunication === sexualCommunication)
			if(index >= 0){
				destArray[index].count++
				return
			}
			destArray.push({
				type: "",
				src: "",
				title: getSexualCommunicationLegendLabel(sexualCommunication),
				count: 1,
				sexualCommunication,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.SEXUAL_COMMUNICATION,
					sexualCommunication,
				},
			})
		},

		buildPrimaryPersonSymbolItem(nodes = []) {
			let primaryPersonNode = null
			for(let node of nodes){
				if(node.data("isAuxNode") || node.data("excludeFromLegend")){
					continue
				}
				if(isPrimaryPersonNodeData(node.data())){
					primaryPersonNode = node
					break
				}
			}
			if(!primaryPersonNode){
				return null
			}

			let type = primaryPersonNode.data("type")
			let src = nodeTypeToImage[type]
			if(!src){
				return null
			}

			return {
				type,
				src,
				title: PRIMARY_PERSON_LABEL,
				isPrimaryPerson: true,
				hoverItem: {kind: LEGEND_HOVER_KIND.PRIMARY_PERSON},
			}
		},

		recalculateGroupsLegend() {
			let nodeGroups = Array.isArray(this.genogramItem?.nodeGroups) ? this.genogramItem.nodeGroups : []
			let groupItems = nodeGroups
				.filter(group => (group.nodeIds || []).length >= 2)
				.map((group, index) => ({
					color: group.color,
					title: (group.title || "").trim() || `Group ${index + 1}`,
					hoverItem: {
						kind: LEGEND_HOVER_KIND.GROUP,
						nodeIds: group.nodeIds || [],
					},
				}))
			groupItems.sort((left, right) => left.title.localeCompare(right.title))
			this.groupItems = groupItems
		},

		changeLegendVisibility() {
			this.isLegendShown = !this.isLegendShown
			this.$emit('legendVisibilityChange', this.isLegendShown)
		},

		onLegendHoverStart(hoverItem) {
			this.hoveredLegendItemKey = this.getLegendItemKey(hoverItem)
			this.applyLegendInteractionState()
		},

		onLegendHoverEnd() {
			this.hoveredLegendItemKey = ""
			this.applyLegendInteractionState()
		},

		onLegendLockToggle(hoverItem) {
			let nextKey = this.getLegendItemKey(hoverItem)
			let existingIndex = this.lockedLegendItems.findIndex((item) => this.getLegendItemKey(item) === nextKey)
			if(existingIndex >= 0){
				this.lockedLegendItems.splice(existingIndex, 1)
			} else {
				this.lockedLegendItems.push(hoverItem)
			}
			this.hoveredLegendItemKey = ""
			this.applyLegendInteractionState()
		},

		clearLockedLegendItem() {
			this.lockedLegendItems = []
			this.hoveredLegendItemKey = ""
			clearLegendHover(this.cy)
			this.notifyPatternExplorationModeChanged()
		},
		clearLegendHoverState() {
			this.clearLockedLegendItem()
		},

		getLegendItemKey(hoverItem) {
			return getLegendHoverItemKey(hoverItem)
		},

		applyLegendInteractionState() {
			applyLegendInteractionState(this.cy, {
				hoveredItem: this.hoveredLegendItemComputed,
				lockedItems: this.lockedLegendItems,
			})
			this.notifyPatternExplorationModeChanged()
		},

		notifyPatternExplorationModeChanged() {
			this.$emit("patternExplorationModeChanged", this.hasLockedLegendItemsComputed)
		},

		getLegendRowClass(hoverItem) {
			let rowKey = this.getLegendItemKey(hoverItem)
			return {
				"legend-row-item--locked": !!rowKey && this.lockedLegendItemKeysComputed.includes(rowKey),
				"legend-row-item--hovered": !!rowKey && rowKey === this.hoveredLegendItemKey && !this.lockedLegendItemKeysComputed.includes(rowKey),
			}
		},

		async requestSnapshot(){
			this.isLegendShown = true
			await this.$nextTickPromise()
			let container = document.getElementById('legendContent')
			let imgBase64
			if(container){
				let html2canvas = (await html2canvasImport()).default
				let canvas = await html2canvas(container, {scale: 2 * window.devicePixelRatio})
				imgBase64 = canvas.toDataURL("image/jpeg", 1);
			}
			console.log("legend requestSnapshot: ", imgBase64)
			return imgBase64
		}
	},

	computed: {
		legendHelperTextComputed() {
			return "Use the legend to explore patterns"
		},
		hideLegendButtonTitle() {
			return this.isLegendShown ? "Hide" : "Show"
		},

		hasLegendItemsToShow() {
			return this.healthConditionItems.length || this.groupItems.length || this.symbolItems.length || this.emotionalConnectionsArray.length || this.familyConnectionsArray.length
		},
		hoveredLegendItemComputed() {
			return [
				...this.familyConnectionsArray,
				...this.emotionalConnectionsArray,
				...this.healthConditionItems,
				...this.groupItems,
				...this.symbolItems,
			].find((item) => this.getLegendItemKey(item.hoverItem) === this.hoveredLegendItemKey)?.hoverItem || null
		},
		lockedLegendItemKeysComputed() {
			return this.lockedLegendItems.map((item) => this.getLegendItemKey(item))
		},
		hasLockedLegendItemsComputed() {
			return this.lockedLegendItems.length > 0
		}
	},
}
</script>

<style lang="scss">
.legend-header{
	margin-bottom: 0;
	border-bottom: 1px solid #e9eef5;
}

.legend-header--collapsed{
	border-bottom-color: transparent;
}

.legend-header__top{
	align-items: flex-start;
}

.legend-header__bottom{
	display: flex;
	align-items: center;
	margin-top: 0;
	margin-bottom: 8px;
	min-height: 16px;
}

.legend-header-copy{
	min-width: 0;
}

.legend-header-copy__title{
	font-size: 14px;
	font-weight: 700;
	line-height: 1.2;
	color: #243b53;
}

.legend-header-copy__helper{
	font-size: 14px;
	line-height: 1.5;
	color: #7b8794;
	max-width: 100%;
}

.legend-toggle-action{
	padding-top: 2px;
}

.legend-toggle-action .link-button{
	font-size: 12px;
	line-height: 1.2;
	color: #7b8794;
}

.legend-section-title{
	font-size: 10px;
	font-weight: 700;
	line-height: 1.2;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: #7b8794;
	margin-bottom: 10px !important;
}

.health-condition-row{
	display: flex;
	align-items: center;
	min-height: 1.4em;
}

.health-condition-dot{
	flex: 0 0 auto;
	width: 0.75em;
	height: 0.75em;
	border-radius: 50%;
	margin-right: 0.5em;
}

.health-condition-text{
	flex: 1 1 auto;
	line-height: 1.4;
}

.symbol-row{
	display: flex;
	align-items: center;
	min-height: 2rem;
	gap: 0.5rem;
}

.symbol-item-image{
}

.symbol-item-text{
	flex: 1 1 auto;
	line-height: 1.4;
}

.symbol-item{
	margin-bottom: 0.5rem;
}

.symbol-item:last-child{
	margin-bottom: 0;
}

.legend-row-item{
	border: 1px solid transparent;
	border-radius: 8px;
	padding-top: 4px;
	padding-bottom: 4px;
	cursor: pointer;
	transition: background-color 0.15s ease, border-color 0.15s ease;
}

.legend-row-item--hovered{
	background: rgba(1, 105, 217, 0.08);
}

.legend-row-item--locked{
	background: rgba(1, 105, 217, 0.14);
	border-color: rgba(1, 105, 217, 0.34);
}

.legend-row-item .line-type-item-view-root .card{
	background: transparent;
	box-shadow: none;
}


</style>
